import { useCallback, useRef, useState } from 'react'

import type { StateMachineData } from '../ProgramStateMachine.types'

const STACK_SIZE = 10

interface UseDefinitionHistoryReturn {
  pushSnapshot: (definition: StateMachineData) => void
  undo: () => StateMachineData | null
  redo: () => StateMachineData | null
  canUndo: boolean
  canRedo: boolean
}

export function useDefinitionHistory(): UseDefinitionHistoryReturn {
  const undoStack = useRef<string[]>([])
  const redoStack = useRef<string[]>([])
  const [, forceUpdate] = useState(0)

  const pushSnapshot = useCallback((definition: StateMachineData) => {
    const json = JSON.stringify(definition)
    const lastSnapshot = undoStack.current[undoStack.current.length - 1]

    if (json === lastSnapshot) return

    undoStack.current.push(json)

    if (undoStack.current.length > STACK_SIZE) {
      undoStack.current.shift()
    }
    redoStack.current = []
    forceUpdate(n => n + 1)
  }, [])

  const undo = useCallback((): StateMachineData | null => {
    if (undoStack.current.length < 2) return null

    const current = undoStack.current.pop()!
    redoStack.current.push(current)

    if (redoStack.current.length > STACK_SIZE) {
      redoStack.current.shift()
    }

    const previous = undoStack.current[undoStack.current.length - 1]
    forceUpdate(n => n + 1)

    return JSON.parse(previous) as StateMachineData
  }, [])

  const redo = useCallback((): StateMachineData | null => {
    if (redoStack.current.length === 0) return null

    const next = redoStack.current.pop()!
    undoStack.current.push(next)
    forceUpdate(n => n + 1)

    return JSON.parse(next) as StateMachineData
  }, [])

  return {
    pushSnapshot,
    undo,
    redo,
    canUndo: undoStack.current.length >= 2,
    canRedo: redoStack.current.length > 0
  }
}
